// frontend/src/components/Dashboard/PreOrderManager.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faSearch,
  faFilter,
  faEye,
  faTimes,
  faTractor,
  faCheckCircle,
  faClock,
  faMoneyBillWave,
  faCalendarCheck,
  faTruck,
  faUser,
  faPhone,
  faMapMarkerAlt,
  faBox,
  faDollarSign,
  faSpinner,
  faWeightScale,
  faGlobe,
  faBoxes,
  faShip,
  faBuilding,
  faAnchor,
  faLocationDot,
  faUserCheck,
  faReceipt,
  faCreditCard,
  faMobileAlt,
  faMoneyBillTransfer,
  faCalendarAlt,
  faCheckDouble,
  faExclamationTriangle,
  faArrowRight,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

// ============================================
// BRAND & SUPPLIER CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    pending: "#D9A321",
    deposit_received: "#8b5cf6",
    confirmed: "#3b82f6",
    ready_for_pickup: "#10b981",
    completed: "#16a34a",
    cancelled: "#dc2626",
    refunded: "#6b7280",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusLabel = (status) => {
  const labels = {
    pending: "⏳ Pending",
    deposit_received: "💵 Deposit Received",
    confirmed: "✅ Confirmed",
    ready_for_pickup: "📦 Ready for Pickup",
    completed: "✅ Completed",
    cancelled: "❌ Cancelled",
    refunded: "↩️ Refunded",
  };
  return labels[status] || status;
};

const getStatusIcon = (status) => {
  const icons = {
    pending: faClock,
    deposit_received: faMoneyBillWave,
    confirmed: faCheckCircle,
    ready_for_pickup: faCalendarCheck,
    completed: faCheckDouble,
    cancelled: faTimes,
    refunded: faTimes,
  };
  return icons[status] || faClock;
};

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount) => {
  if (!amount) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

// ============================================
// MAIN COMPONENT
// ============================================
const PreOrderManager = ({
  preOrders,
  stats,
  onUpdateStatus,
  onRefresh,
  availableSizes = [],
  preOrderSizes = [],
}) => {
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPreOrder, setSelectedPreOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  // Use passed sizes or defaults
  const sizes = preOrderSizes.length > 0 ? preOrderSizes : [25, 50];

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "pending", label: "⏳ Pending" },
    { value: "deposit_received", label: "💵 Deposit Received" },
    { value: "confirmed", label: "✅ Confirmed" },
    { value: "ready_for_pickup", label: "📦 Ready for Pickup" },
    { value: "completed", label: "✅ Completed" },
    { value: "cancelled", label: "❌ Cancelled" },
    { value: "refunded", label: "↩️ Refunded" },
  ];

  const filteredPreOrders = preOrders
    .filter((p) => !filterStatus || p.status === filterStatus)
    .filter((p) => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        p.preOrderNumber?.toLowerCase().includes(search) ||
        p.customerInfo?.fullName?.toLowerCase().includes(search) ||
        p.customerInfo?.phone?.includes(search) ||
        p.customerInfo?.farmOrBusinessName?.toLowerCase().includes(search)
      );
    });

  const handleStatusUpdate = async (id, status) => {
    setLoading(true);
    try {
      await onUpdateStatus(id, { status });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error updating pre-order status:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: "Cash",
      mobile_money: "Mobile Money",
      orange_money: "Orange Money",
      afrimoney: "Afrimoney",
      wave: "WAVE",
      bank_transfer: "Bank Transfer",
      credit_card: "Credit Card",
    };
    return labels[method] || method || "N/A";
  };

  return (
    <div className="preorder-manager">
      {/* ========== STATS SUMMARY ========== */}
      <div className="preorder-stats-grid">
        <div className="preorder-stat-card">
          <div className="stat-value">{stats.totalPreOrders || 0}</div>
          <div className="stat-label">Total Pre-Orders</div>
        </div>
        <div className="preorder-stat-card" style={{ borderColor: "#D9A321" }}>
          <div className="stat-value" style={{ color: "#D9A321" }}>
            {stats.totalRevenueUsd
              ? formatCurrency(stats.totalRevenueUsd)
              : "$0"}
          </div>
          <div className="stat-label">Total Revenue</div>
        </div>
        <div className="preorder-stat-card" style={{ borderColor: "#8b5cf6" }}>
          <div className="stat-value" style={{ color: "#8b5cf6" }}>
            {stats.totalDepositUsd
              ? formatCurrency(stats.totalDepositUsd)
              : "$0"}
          </div>
          <div className="stat-label">Total Deposits</div>
        </div>
        <div className="preorder-stat-card" style={{ borderColor: "#3b82f6" }}>
          <div className="stat-value" style={{ color: "#3b82f6" }}>
            {stats.totalBalanceUsd
              ? formatCurrency(stats.totalBalanceUsd)
              : "$0"}
          </div>
          <div className="stat-label">Balance Due</div>
        </div>
        <div className="preorder-stat-card" style={{ borderColor: "#10b981" }}>
          <div className="stat-value">{stats.totalPacks || 0}</div>
          <div className="stat-label">Total Packs</div>
        </div>
        <div
          className="preorder-stat-card"
          style={{ borderColor: "#006B2E", background: "#e8f5e9" }}
        >
          <div
            className="stat-value"
            style={{ fontSize: "1rem", color: "#006B2E" }}
          >
            <FontAwesomeIcon icon={faWeightScale} /> {sizes.join("kg, ")}kg
          </div>
          <div className="stat-label">
            <FontAwesomeIcon icon={faShip} /> Pre-Order Sizes
          </div>
        </div>
      </div>

      {/* ========== SUPPLIER INFO ========== */}
      <div className="supplier-info-card">
        <FontAwesomeIcon icon={faShip} className="supplier-icon" />
        <div className="supplier-info">
          <strong>Sourced from {SUPPLIER.name}</strong>
          <span>
            <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} &bull;
            <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
          </span>
        </div>
        <span className="supplier-badge">
          <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
        </span>
      </div>

      {/* ========== FILTERS ========== */}
      <div className="section-header">
        <h2>
          <FontAwesomeIcon icon={faClipboardList} /> Pre-Orders
          <span className="origin-badge">
            <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} Origin
          </span>
        </h2>
        <div className="filters">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, phone, or order #"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {/* ========== TABLE ========== */}
      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th>Order #</th>
              <th>Customer</th>
              <th>Phone</th>
              <th>Location</th>
              <th>Items</th>
              <th>Total</th>
              <th>Deposit</th>
              <th>Balance</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPreOrders.length === 0 ? (
              <tr>
                <td colSpan="11" className="no-data">
                  <div className="empty-state">
                    <FontAwesomeIcon icon={faClipboardList} size="2x" />
                    <p>No pre-orders found.</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredPreOrders.map((preOrder) => (
                <tr
                  key={preOrder._id}
                  className={preOrder.status === "pending" ? "pending-row" : ""}
                >
                  <td>
                    <strong>{preOrder.preOrderNumber}</strong>
                    <span className="size-badge">
                      <FontAwesomeIcon icon={faBoxes} />{" "}
                      {preOrder.totalPacks || 0} packs
                    </span>
                  </td>
                  <td>
                    <div className="customer-info">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="customer-icon"
                      />
                      {preOrder.customerInfo?.fullName || "N/A"}
                      {preOrder.customerInfo?.farmOrBusinessName && (
                        <div className="farm-name">
                          <FontAwesomeIcon icon={faTractor} />{" "}
                          {preOrder.customerInfo.farmOrBusinessName}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <FontAwesomeIcon icon={faPhone} className="phone-icon" />
                    <a
                      href={`tel:${preOrder.customerInfo?.phone}`}
                      className="phone-link"
                    >
                      {preOrder.customerInfo?.phone || "N/A"}
                    </a>
                    {preOrder.customerInfo?.whatsapp && (
                      <a
                        href={`https://wa.me/${preOrder.customerInfo.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whatsapp-link"
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </a>
                    )}
                  </td>
                  <td>
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className="location-icon"
                    />
                    {preOrder.customerInfo?.location || "N/A"}
                    {preOrder.customerInfo?.district && (
                      <span className="district-tag">
                        {preOrder.customerInfo.district}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="items-info">
                      <FontAwesomeIcon icon={faBox} />
                      {preOrder.totalPacks || 0} packs
                      <span className="item-detail">
                        ({preOrder.totalQuantityKg || 0} kg)
                      </span>
                    </div>
                  </td>
                  <td>
                    <strong className="total-amount">
                      {formatCurrency(preOrder.subtotal?.usd)}
                    </strong>
                  </td>
                  <td className="deposit-amount">
                    {formatCurrency(preOrder.depositRequired?.usd)}
                    {preOrder.payment?.status === "deposit_received" && (
                      <span className="deposit-received-badge">✓</span>
                    )}
                  </td>
                  <td className="balance-amount">
                    {formatCurrency(preOrder.balanceDue?.usd)}
                  </td>
                  <td>
                    <span
                      className="status-badge"
                      style={{ background: getStatusColor(preOrder.status) }}
                    >
                      <FontAwesomeIcon icon={getStatusIcon(preOrder.status)} />
                      {getStatusLabel(preOrder.status)}
                    </span>
                  </td>
                  <td>{formatDate(preOrder.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <select
                        value={preOrder.status}
                        onChange={(e) =>
                          handleStatusUpdate(preOrder._id, e.target.value)
                        }
                        className="status-select"
                        style={{
                          borderColor: getStatusColor(preOrder.status),
                          background: `${getStatusColor(preOrder.status)}10`,
                        }}
                        disabled={loading}
                      >
                        <option value="pending">⏳ Pending</option>
                        <option value="deposit_received">
                          💵 Deposit Received
                        </option>
                        <option value="confirmed">✅ Confirmed</option>
                        <option value="ready_for_pickup">
                          📦 Ready for Pickup
                        </option>
                        <option value="completed">✅ Completed</option>
                        <option value="cancelled">❌ Cancelled</option>
                        <option value="refunded">↩️ Refunded</option>
                      </select>
                      <button
                        className="view-btn"
                        onClick={() => setSelectedPreOrder(preOrder)}
                        title="View Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ========== DETAILS MODAL ========== */}
      {selectedPreOrder && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedPreOrder(null)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faClipboardList} /> Pre-Order Details
                <span className="order-number">
                  {selectedPreOrder.preOrderNumber}
                </span>
                <span className="origin-badge">
                  <FontAwesomeIcon icon={faShip} /> {SUPPLIER.country} Origin
                </span>
              </h2>
              <button
                className="close-btn"
                onClick={() => setSelectedPreOrder(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              {/* Customer Info */}
              <div className="detail-section">
                <h3>
                  <FontAwesomeIcon icon={faUser} /> Customer Information
                </h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Full Name:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.fullName}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Farm/Business:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.farmOrBusinessName ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">
                      <a
                        href={`tel:${selectedPreOrder.customerInfo?.phone}`}
                        className="phone-link"
                      >
                        {selectedPreOrder.customerInfo?.phone}
                      </a>
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">WhatsApp:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.whatsapp ? (
                        <a
                          href={`https://wa.me/${selectedPreOrder.customerInfo.whatsapp}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="whatsapp-link"
                        >
                          <FontAwesomeIcon icon={faWhatsapp} />{" "}
                          {selectedPreOrder.customerInfo.whatsapp}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.email || "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.location}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">District:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.district || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Farm Info */}
              <div className="detail-section">
                <h3>
                  <FontAwesomeIcon icon={faTractor} /> Farm Information
                </h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Customer Type:</span>
                    <span className="detail-value">
                      {selectedPreOrder.farmInfo?.customerType?.replace(
                        /_/g,
                        " ",
                      ) || "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Animal Types:</span>
                    <span className="detail-value">
                      {selectedPreOrder.farmInfo?.animalTypes?.join(", ") ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Animal Count:</span>
                    <span className="detail-value">
                      {selectedPreOrder.farmInfo?.animalCount || 0}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Feed Mixed at Once:</span>
                    <span className="detail-value">
                      {selectedPreOrder.farmInfo?.feedMixedAtOneTimeKg || 0} kg
                    </span>
                  </div>
                  {selectedPreOrder.farmInfo?.notes && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Notes:</span>
                      <span className="detail-value">
                        {selectedPreOrder.farmInfo.notes}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Items */}
              <div className="detail-section">
                <h3>
                  <FontAwesomeIcon icon={faBox} /> Pre-Order Items
                </h3>
                <div className="table-responsive">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Pack Size</th>
                        <th>Qty</th>
                        <th>Regular Price</th>
                        <th>Pre-Order Price</th>
                        <th>Line Total</th>
                        <th>Deposit</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPreOrder.items?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.productName}</td>
                          <td>{item.packSizeKg} kg</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.regularPrice?.usd)}</td>
                          <td>{formatCurrency(item.preOrderPrice?.usd)}</td>
                          <td>{formatCurrency(item.lineTotal?.usd)}</td>
                          <td>{formatCurrency(item.depositRequired?.usd)}</td>
                          <td>{formatCurrency(item.balanceDue?.usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="5"></td>
                        <td>
                          <strong>
                            Total:{" "}
                            {formatCurrency(selectedPreOrder.subtotal?.usd)}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {formatCurrency(
                              selectedPreOrder.depositRequired?.usd,
                            )}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {formatCurrency(selectedPreOrder.balanceDue?.usd)}
                          </strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="summary-box">
                  <span>
                    <FontAwesomeIcon icon={faBoxes} /> Total Packs:{" "}
                    {selectedPreOrder.totalPacks || 0}
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faWeightScale} /> Total Weight:{" "}
                    {selectedPreOrder.totalQuantityKg || 0} kg
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} Origin
                  </span>
                </div>
              </div>

              {/* Payment & Pickup */}
              <div className="detail-section">
                <h3>
                  <FontAwesomeIcon icon={faMoneyBillWave} /> Payment & Pickup
                </h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Payment Method:</span>
                    <span className="detail-value">
                      <FontAwesomeIcon icon={faCreditCard} />{" "}
                      {getPaymentMethodLabel(selectedPreOrder.payment?.method)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Payment Status:</span>
                    <span
                      className={`payment-status ${selectedPreOrder.payment?.status}`}
                    >
                      {getStatusLabel(selectedPreOrder.payment?.status)}
                    </span>
                  </div>
                  {selectedPreOrder.payment?.transactionId && (
                    <div className="detail-item">
                      <span className="detail-label">Transaction ID:</span>
                      <span className="detail-value">
                        {selectedPreOrder.payment.transactionId}
                      </span>
                    </div>
                  )}
                  {selectedPreOrder.payment?.paymentReference && (
                    <div className="detail-item">
                      <span className="detail-label">Payment Reference:</span>
                      <span className="detail-value">
                        {selectedPreOrder.payment.paymentReference}
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Pickup Type:</span>
                    <span className="detail-value">
                      {selectedPreOrder.pickup?.type?.replace(/_/g, " ") ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Pickup Address:</span>
                    <span className="detail-value">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                      {selectedPreOrder.pickup?.address || "N/A"}
                    </span>
                  </div>
                  {selectedPreOrder.pickup?.preferredDate && (
                    <div className="detail-item">
                      <span className="detail-label">
                        Preferred Pickup Date:
                      </span>
                      <span className="detail-value">
                        {formatDate(selectedPreOrder.pickup.preferredDate)}
                      </span>
                    </div>
                  )}
                  {selectedPreOrder.pickup?.instructions && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Pickup Instructions:</span>
                      <span className="detail-value">
                        {selectedPreOrder.pickup.instructions}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div className="detail-section timeline-section">
                <h3>
                  <FontAwesomeIcon icon={faClock} /> Timeline
                </h3>
                <div className="timeline">
                  {selectedPreOrder.timeline?.map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div
                        className="timeline-dot"
                        style={{ background: getStatusColor(event.status) }}
                      />
                      <div className="timeline-content">
                        <div className="timeline-status">
                          {getStatusLabel(event.status)}
                          {event.updatedBy && (
                            <span className="updated-by">
                              by {event.updatedBy}
                            </span>
                          )}
                        </div>
                        {event.description && (
                          <div className="timeline-description">
                            {event.description}
                          </div>
                        )}
                        <div className="timeline-date">
                          {formatDate(event.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              {(selectedPreOrder.customerNotes ||
                selectedPreOrder.adminNotes) && (
                <div className="detail-section notes-section">
                  <h3>
                    <FontAwesomeIcon icon={faInfoCircle} /> Notes
                  </h3>
                  {selectedPreOrder.customerNotes && (
                    <div className="note-item customer-note">
                      <span className="note-label">Customer:</span>
                      <p>{selectedPreOrder.customerNotes}</p>
                    </div>
                  )}
                  {selectedPreOrder.adminNotes && (
                    <div className="note-item admin-note">
                      <span className="note-label">Admin:</span>
                      <p>{selectedPreOrder.adminNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setSelectedPreOrder(null)}
              >
                Close
              </button>
              <a
                href={`https://wa.me/${selectedPreOrder.customerInfo?.phone || "23231030808"}?text=Hi%20${encodeURIComponent(selectedPreOrder.customerInfo?.fullName || "")}%2C%20this%20is%20Kenali%20Enterprise%20regarding%20your%20pre-order%20${encodeURIComponent(selectedPreOrder.preOrderNumber || "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
              >
                <FontAwesomeIcon icon={faWhatsapp} /> Contact Customer
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        .preorder-manager { padding: 0.5rem 0; }

        /* Stats Grid */
        .preorder-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .preorder-stat-card {
          background: #fff;
          padding: 1rem;
          border-radius: 12px;
          border: 1px solid #e5e5e5;
          text-align: center;
          transition: all 0.3s ease;
        }

        .preorder-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.06);
        }

        .preorder-stat-card .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #006b2e;
          font-family: "Montserrat", sans-serif;
        }

        .preorder-stat-card .stat-label {
          font-size: 0.7rem;
          color: #5a5a5a;
          margin-top: 0.25rem;
        }

        /* Supplier Info */
        .supplier-info-card {
          background: linear-gradient(135deg, #006B2E, #014421);
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 1rem;
          color: #ffffff;
          margin-bottom: 1.5rem;
        }

        .supplier-info-card .supplier-icon {
          font-size: 1.5rem;
          color: #D9A321;
        }

        .supplier-info-card .supplier-info {
          flex: 1;
        }

        .supplier-info-card .supplier-info strong {
          font-size: 0.875rem;
          font-family: "Montserrat", sans-serif;
        }

        .supplier-info-card .supplier-info span {
          font-size: 0.65rem;
          opacity: 0.8;
          display: block;
        }

        .supplier-info-card .supplier-info span svg {
          margin: 0 0.25rem;
        }

        .supplier-badge {
          font-size: 0.6rem;
          background: rgba(217, 163, 33, 0.2);
          color: #D9A321;
          padding: 0.15rem 0.6rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        /* Section Header */
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .section-header h2 {
          color: #006b2e;
          font-size: 1.1rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }

        .origin-badge {
          font-size: 0.65rem;
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          background: #dcfce7;
          color: #16a34a;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        .size-badge {
          font-size: 0.6rem;
          padding: 0.1rem 0.4rem;
          border-radius: 10px;
          background: #ede9fe;
          color: #7c3aed;
          margin-left: 0.25rem;
          display: inline-flex;
          align-items: center;
          gap: 0.15rem;
        }

        .filters {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .filter-select {
          padding: 0.3rem 0.75rem;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          font-size: 0.8rem;
          background: #fff;
          font-family: "Inter", sans-serif;
          min-height: 38px;
        }

        .filter-select:focus {
          outline: none;
          border-color: #D9A321;
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 0.5rem;
          color: #9ca3af;
        }

        .search-input {
          padding: 0.3rem 0.75rem 0.3rem 2rem;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          font-size: 0.8rem;
          width: 200px;
          font-family: "Inter", sans-serif;
          min-height: 38px;
        }

        .search-input:focus {
          outline: none;
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }

        /* Table */
        .table-responsive {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
          min-width: 1000px;
        }

        .data-table th {
          background: #f8f9fa;
          padding: 0.5rem 0.75rem;
          text-align: left;
          font-weight: 600;
          color: #5a5a5a;
          border-bottom: 2px solid #e5e5e5;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .data-table td {
          padding: 0.5rem 0.75rem;
          border-bottom: 1px solid #e5e5e5;
          vertical-align: middle;
        }

        .data-table tr:hover { background: #f8f9fa; }
        .data-table tr.pending-row { background: rgba(217, 163, 33, 0.03); }

        .no-data { text-align: center; padding: 2rem; color: #9ca3af; }
        .empty-state { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }

        .customer-info { display: flex; flex-direction: column; }
        .customer-icon { color: #3b82f6; margin-right: 0.25rem; }
        .farm-name { font-size: 0.65rem; color: #6b7280; margin-left: 1.25rem; }

        .phone-icon { color: #10b981; margin-right: 0.25rem; }
        .phone-link { color: #006B2E; text-decoration: none; }
        .phone-link:hover { text-decoration: underline; }

        .whatsapp-link {
          display: inline-flex;
          align-items: center;
          color: #25d366;
          margin-left: 0.25rem;
          transition: all 0.3s ease;
        }

        .whatsapp-link:hover { transform: scale(1.15); }

        .location-icon { color: #8b5cf6; margin-right: 0.25rem; }
        .district-tag {
          font-size: 0.55rem;
          background: #f3f4f6;
          color: #6b7280;
          padding: 0.05rem 0.3rem;
          border-radius: 8px;
          margin-left: 0.25rem;
        }

        .items-info { display: flex; align-items: center; gap: 0.25rem; }
        .item-detail { font-size: 0.65rem; color: #9ca3af; }

        .status-badge {
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          color: #fff;
          font-size: 0.65rem;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        .total-amount { color: #006b2e; }
        .deposit-amount { color: #8b5cf6; }
        .balance-amount { color: #d9a321; }
        .deposit-received-badge { color: #16a34a; margin-left: 0.25rem; }

        .status-select {
          padding: 0.15rem 0.4rem;
          border-radius: 4px;
          border: 1px solid;
          font-size: 0.65rem;
          cursor: pointer;
          min-width: 100px;
          font-family: "Inter", sans-serif;
        }

        .view-btn {
          background: none;
          border: none;
          color: #3b82f6;
          cursor: pointer;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          transition: all 0.3s ease;
        }

        .view-btn:hover { background: #dbeafe; }

        .action-buttons { display: flex; align-items: center; gap: 0.25rem; }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: #fff;
          border-radius: 16px;
          max-width: 900px;
          width: 100%;
          max-height: 95vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.25);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e5e5;
          flex-shrink: 0;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .modal-header h2 {
          color: #006b2e;
          font-size: 1.1rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          font-family: "Montserrat", sans-serif;
        }

        .order-number { font-size: 0.8rem; color: #5a5a5a; font-weight: 400; }

        .modal-body { padding: 1.5rem; overflow-y: auto; flex: 1; }
        .modal-footer {
          padding: 1rem 1.5rem;
          border-top: 1px solid #e5e5e5;
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          color: #6b7280;
          padding: 0.25rem;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .close-btn:hover { background: #f3f4f6; color: #1f1f1f; }

        .btn-secondary {
          padding: 0.4rem 1.2rem;
          background: #f0f0f0;
          color: #1f1f1f;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-family: "Inter", sans-serif;
        }

        .btn-secondary:hover { background: #e5e5e5; }

        .btn-whatsapp {
          padding: 0.4rem 1.2rem;
          background: #25d366;
          color: #fff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: "Inter", sans-serif;
          transition: all 0.3s ease;
        }

        .btn-whatsapp:hover { background: #1da851; transform: translateY(-2px); }

        /* Detail Sections */
        .detail-section {
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #f0f0f0;
        }

        .detail-section:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }

        .detail-section h3 {
          color: #006b2e;
          font-size: 0.95rem;
          margin-bottom: 0.75rem;
          font-family: "Montserrat", sans-serif;
        }

        .detail-section h3 svg { margin-right: 0.5rem; color: #D9A321; }

        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.5rem;
        }

        .detail-item { display: flex; flex-direction: column; padding: 0.25rem 0; }
        .detail-item.full-width { grid-column: 1 / -1; }
        .detail-label { font-size: 0.65rem; color: #9ca3af; font-weight: 500; }
        .detail-value { font-size: 0.85rem; color: #1f1f1f; }

        .payment-status {
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 500;
          display: inline-block;
        }

        .payment-status.pending { background: #fef3c7; color: #d97706; }
        .payment-status.deposit_received { background: #ede9fe; color: #7c3aed; }
        .payment-status.paid { background: #dcfce7; color: #16a34a; }
        .payment-status.failed { background: #fee2e2; color: #dc2626; }
        .payment-status.refunded { background: #f3f4f6; color: #6b7280; }

        .summary-box {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          padding: 0.75rem 1rem;
          background: #f8f9fa;
          border-radius: 6px;
          margin-top: 0.5rem;
          font-size: 0.85rem;
          color: #1f1f1f;
        }

        .summary-box svg { color: #006b2e; margin-right: 0.25rem; }

        .items-table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
        .items-table th { background: #f8f9fa; padding: 0.3rem 0.5rem; text-align: left; font-weight: 600; color: #5a5a5a; }
        .items-table td { padding: 0.3rem 0.5rem; border-bottom: 1px solid #f0f0f0; }
        .items-table tfoot td { font-weight: 600; padding-top: 0.5rem; border-top: 2px solid #e5e5e5; }

        .timeline { position: relative; padding-left: 1.5rem; }
        .timeline::before {
          content: '';
          position: absolute;
          left: 6px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: #e5e5e5;
        }

        .timeline-item {
          position: relative;
          padding-bottom: 1rem;
          display: flex;
          gap: 0.75rem;
        }

        .timeline-item:last-child { padding-bottom: 0; }

        .timeline-dot {
          position: absolute;
          left: -1.25rem;
          top: 2px;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid #fff;
          flex-shrink: 0;
        }

        .timeline-content { flex: 1; }
        .timeline-status { font-weight: 600; font-size: 0.85rem; }
        .updated-by { font-weight: 400; font-size: 0.7rem; color: #9ca3af; margin-left: 0.25rem; }
        .timeline-description { font-size: 0.8rem; color: #5a5a5a; margin-top: 0.1rem; }
        .timeline-date { font-size: 0.7rem; color: #9ca3af; margin-top: 0.1rem; }

        .note-item {
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          margin-bottom: 0.5rem;
        }

        .note-item:last-child { margin-bottom: 0; }
        .customer-note { background: #f0f9ff; border-left: 3px solid #3b82f6; }
        .admin-note { background: #fef3c7; border-left: 3px solid #d9a321; }
        .note-label { font-weight: 600; font-size: 0.7rem; color: #5a5a5a; }
        .note-item p { margin: 0.25rem 0 0; font-size: 0.85rem; }

        /* Responsive */
        @media (max-width: 768px) {
          .preorder-stats-grid { grid-template-columns: repeat(3, 1fr); }
          .detail-grid { grid-template-columns: 1fr; }
          .section-header { flex-direction: column; align-items: stretch; }
          .filters { flex-direction: column; }
          .search-input { width: 100%; }
          .data-table { font-size: 0.7rem; min-width: 800px; }
          .data-table th, .data-table td { padding: 0.3rem 0.4rem; }
          .modal-content { max-width: 100%; margin: 0.5rem; }
          .supplier-info-card { flex-wrap: wrap; justify-content: center; text-align: center; }
        }

        @media (max-width: 480px) {
          .preorder-stats-grid { grid-template-columns: 1fr 1fr; }
          .summary-box { flex-direction: column; gap: 0.25rem; }
          .modal-header h2 { font-size: 0.95rem; }
          .modal-body { padding: 1rem; }
          .modal-footer { flex-direction: column; }
          .btn-whatsapp { width: 100%; justify-content: center; }
          .supplier-info-card { flex-direction: column; }
        }
      `}</style>
    </div>
  );
};

export default PreOrderManager;
